import { httpClient } from "@/utils/api/client";

export type AlphaAuditStartResponse = { auditId: string; room?: string };

export type AlphaAuditReadyPayload = { auditId: string; downloadUrl: string };
export type AlphaAuditErrorPayload = { auditId: string; message?: string };

export type DirectAlphaAuditResponse<TData = unknown> = {
  message?: string;
  data?: TData;
};

export type SocketLike = {
  emit: (event: string, ...args: any[]) => void;
  on: (event: string, cb: (...args: any[]) => void) => void;
  off: (event: string, cb?: (...args: any[]) => void) => void;
  connected?: boolean;
};

export type SocketFactory = () => Promise<SocketLike>;

export class AlphaAuditClient {
  private pending = new Map<
    string,
    {
      resolve: (v: AlphaAuditReadyPayload) => void;
      reject: (e: Error) => void;
      timeoutId: number;
      onReady: (p: AlphaAuditReadyPayload) => void;
      onError: (p: AlphaAuditErrorPayload) => void;
    }
  >();

  private socketPromise: Promise<SocketLike> | null = null;
  private socket: SocketLike | null = null;
  private disconnectHandlerRegistered = false;

  constructor(
    private getSocket: SocketFactory,
    private defaultTimeoutMs = 4 * 60 * 1000
  ) {}

  private async ensureSocket(): Promise<SocketLike> {
    if (this.socket) return this.socket;

    if (!this.socketPromise) {
      this.socketPromise = this.getSocket().then((s) => {
        this.socket = s;

        if (!this.disconnectHandlerRegistered) {
          this.disconnectHandlerRegistered = true;
          s.on("disconnect", () => {
            this.pending.forEach((_v, auditId) => {
              const entry = this.pending.get(auditId);
              if (!entry) return;

              try {
                entry.reject(new Error("Socket disconnected"));
              } catch {}

              this.cleanup(s, auditId);
            });
          });
        }

        return s;
      });
    }

    return this.socketPromise;
  }

  private async postJson<TResponse>(path: string, body: unknown): Promise<TResponse> {
    const { data } = await httpClient.post<TResponse>(path, body);
    return data;
  }

  /**
   * Existing async audit flow:
   * POST -> auditId -> WS -> downloadUrl
   */
  async startAudit(
    functionName: string,
    params: any,
    timeoutMs: number = this.defaultTimeoutMs
  ): Promise<AlphaAuditReadyPayload> {
    const { data } = await httpClient.post<AlphaAuditStartResponse>("/agent/alpha-audit", {
      function: functionName,
      params,
    });

    const { auditId } = data;
    if (!auditId) {
      throw new Error("Alpha audit response missing auditId");
    }

    const socket = await this.ensureSocket();
    return this.waitForAudit(socket, auditId, timeoutMs);
  }

  /**
   * Generic direct audit flow:
   * POST -> immediate JSON response
   */
  async startDirectAudit<TData = unknown>(
    functionName: string,
    params: any,
    path: string = "/agent/alpha-audit"
  ): Promise<DirectAlphaAuditResponse<TData>> {
    return this.postJson<DirectAlphaAuditResponse<TData>>(path, {
      function: functionName,
      params,
    });
  }

  private waitForAudit(socket: SocketLike, auditId: string, timeoutMs: number): Promise<AlphaAuditReadyPayload> {
    if (this.pending.has(auditId)) {
      return Promise.reject(new Error(`Alpha audit already pending for auditId=${auditId}`));
    }

    return new Promise<AlphaAuditReadyPayload>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        this.cleanup(socket, auditId);
        this.safeLeave(socket, auditId);
        reject(new Error(`Alpha audit timed out (auditId=${auditId})`));
      }, timeoutMs);

      const onReady = (payload: AlphaAuditReadyPayload) => {
        if (payload?.auditId !== auditId) return;
        const entry = this.pending.get(auditId);
        if (!entry) return;

        entry.resolve(payload);
        this.cleanup(socket, auditId);
        this.safeLeave(socket, auditId);
      };

      const onError = (payload: AlphaAuditErrorPayload) => {
        if (payload?.auditId !== auditId) return;
        const entry = this.pending.get(auditId);
        if (!entry) return;

        entry.reject(new Error(payload?.message ?? "Alpha audit failed"));
        this.cleanup(socket, auditId);
        this.safeLeave(socket, auditId);
      };

      this.pending.set(auditId, {
        resolve: (v) => {
          window.clearTimeout(timeoutId);
          resolve(v);
        },
        reject: (e) => {
          window.clearTimeout(timeoutId);
          reject(e);
        },
        timeoutId,
        onReady,
        onError,
      });

      socket.on("alphaAuditReady", onReady);
      socket.on("alphaAuditError", onError);

      socket.emit("joinAlphaAuditRoom", auditId);
    });
  }

  private cleanup(socket: SocketLike, auditId: string): void {
    const entry = this.pending.get(auditId);
    if (!entry) return;

    socket.off("alphaAuditReady", entry.onReady);
    socket.off("alphaAuditError", entry.onError);
    window.clearTimeout(entry.timeoutId);

    this.pending.delete(auditId);
  }

  private safeLeave(socket: SocketLike, auditId: string): void {
    socket.emit("leaveAlphaAuditRoom", auditId);
  }
}
